import React, { useState, useEffect } from 'react'
import MaintenanceTypeForm from '../components/MaintenanceTypeForm'
import { apiService } from '../services/apiService'
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  WrenchScrewdriverIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

const MaintenanceTypesPage = () => {
  const [maintenanceTypes, setMaintenanceTypes] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editingType, setEditingType] = useState(null)

  useEffect(() => {
    loadMaintenanceTypes()
  }, [])

  const loadMaintenanceTypes = async () => {
    try {
      setLoading(true)
      const result = await apiService.getMaintenanceTypes()
      setMaintenanceTypes(result.data || [])
    } catch (error) {
      console.error('Erro ao carregar tipos de manutenção:', error)
      setMaintenanceTypes([])
      toast.error('Erro ao carregar tipos de manutenção')
    } finally {
      setLoading(false)
    }
  }

  const handleAdd = () => {
    setEditingType(null)
    setShowForm(true)
  }

  const handleEdit = (maintenanceType) => {
    setEditingType(maintenanceType)
    setShowForm(true)
  }

  const handleView = (maintenanceType) => {
    toast(`Visualizando tipo: ${maintenanceType.name}`, {
      icon: 'ℹ️',
    })
  }

  const handleDelete = async (id, name) => {
    if (!confirm(`Tem certeza que deseja desativar o tipo de manutenção "${name}"?`)) {
      return
    }

    try {
      await apiService.deleteMaintenanceType(id)
      toast.success('Tipo de manutenção desativado com sucesso!')
      loadMaintenanceTypes()
    } catch (error) {
      console.error('Erro ao desativar tipo de manutenção:', error)
      toast.error('Erro ao desativar tipo de manutenção')
    }
  }

  const handleFormClose = () => {
    setShowForm(false)
    setEditingType(null)
  }

  const handleFormSuccess = () => {
    loadMaintenanceTypes()
    setShowForm(false)
    setEditingType(null)
  }

  const filteredTypes = maintenanceTypes.filter(type => {
    const searchLower = searchQuery.toLowerCase()
    return (
      type.name.toLowerCase().includes(searchLower) ||
      type.category.toLowerCase().includes(searchLower)
    )
  })

  const getPriorityColor = (priority) => {
    const colors = {
      'low': 'bg-green-100 text-green-800',
      'normal': 'bg-blue-100 text-blue-800',
      'high': 'bg-yellow-100 text-yellow-800',
      'urgent': 'bg-red-100 text-red-800'
    }
    return colors[priority] || 'bg-gray-100 text-gray-800'
  }

  const getPriorityLabel = (priority) => {
    const labels = {
      'low': 'Baixa',
      'normal': 'Normal',
      'high': 'Alta',
      'urgent': 'Urgente'
    }
    return labels[priority] || priority
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Carregando tipos de manutenção...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <WrenchScrewdriverIcon className="h-8 w-8 text-blue-600 mr-3" />
            Tipos de Manutenção
          </h1>
          <p className="text-gray-600 mt-1">
            Gerencie os tipos de manutenção dos veículos
          </p>
        </div>

        <button
          onClick={handleAdd}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Novo Tipo</span>
        </button>
      </div>

      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="relative">
          <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por nome ou categoria..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        {filteredTypes.length === 0 ? (
          <div className="text-center py-12">
            <Cog6ToothIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchQuery ? 'Nenhum tipo encontrado' : 'Backend implementado'}
            </h3>
            <p className="text-gray-500 mb-4">
              API funcionando. Frontend completo em desenvolvimento.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nome
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Categoria
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Prioridade
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duração Estimada
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTypes.map((type) => (
                  <tr key={type.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{type.name}</div>
                      {type.description && (
                        <div className="text-sm text-gray-500">{type.description}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {type.category}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(type.priority)}`}>
                        {getPriorityLabel(type.priority)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {type.estimated_duration || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleView(type)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded"
                          title="Visualizar"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(type)}
                          className="text-indigo-600 hover:text-indigo-900 p-1 rounded"
                          title="Editar"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(type.id, type.name)}
                          className="text-red-600 hover:text-red-900 p-1 rounded"
                          title="Desativar"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal do Formulário */}
      {showForm && (
        <MaintenanceTypeForm
          maintenanceType={editingType}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  )
}

export default MaintenanceTypesPage